import React, { useState } from 'react';
import useLanguagesHooks from '../../../../../../../../Components/Languages/setLanguage-hooks';
import Button from '../../../../../../../../Components/Button/button';
import { ButtonTypes, HorecaLocationType } from '../../../../../../../../Interfaces/componentTypes';
import { PencilIcon } from '../../../../../../../../Components/IconComponents';
import { PlusIcon } from '../../../../../../../../Assets/Icons';
import NoData from '../../../../../../../../Components/NoData/noData';
import CustomDialog from '../../../../../../../../Components/Dialog/customDialog';
import { IDineInUnit } from '../../../../../../../../Interfaces/responseTypes';
import AddUnitDialog from './AddUnitDialog/addUnitDialog';
import DeleteUnitDialog from './DeleteUnitDialog/deleteUnitDialog';
import UnitQrStatusBadge from './UnitQrStatusBadge/unitQrStatusBadge';
import {
    createNewDineInUnit,
    getActiveDineInUnits,
    getTotalDineInCapacity,
    isActiveDineInUnit,
    isUnitQrGenerated,
    mergeEditedUnit,
} from './dineInHelpers';
import styles from './dineInUnits.module.scss';

interface IDineInUnitsProps {
    units: IDineInUnit[];
    onUnitsChange: (units: IDineInUnit[]) => void;
}

const getUnitDisplayTitle = (
    unit: IDineInUnit,
    t: (key: string) => string
): string => {
    const typeLabel = t(`My_Business.My_Services.Dine_In.Unit_Types.${unit.type}`);
    return `${typeLabel} × ${unit.quantity}`;
};

const DineInUnits: React.FC<IDineInUnitsProps> = ({ units, onUnitsChange }) => {
    const { t } = useLanguagesHooks();
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

    const editingUnit = editingIndex !== null ? units[editingIndex] : null;
    const deletingUnit = deletingIndex !== null ? units[deletingIndex] : null;
    const activeUnits = getActiveDineInUnits(units);
    const allUnitTypesUsed = activeUnits.length >= Object.values(HorecaLocationType).length;
    const totalCapacity = getTotalDineInCapacity(units);
    const generatedUnitsCount = activeUnits.filter(isUnitQrGenerated).length;

    const handleOpenAddDialog = () => {
        setEditingIndex(null);
        setAddDialogOpen(true);
    };

    const handleOpenEditDialog = (index: number) => {
        setEditingIndex(index);
        setAddDialogOpen(true);
    };

    const handleCloseAddDialog = () => {
        setAddDialogOpen(false);
        setEditingIndex(null);
    };

    const handleSaveUnit = (unit: IDineInUnit) => {
        const isDuplicateType = units.some(
            (existingUnit, index) =>
                existingUnit.type === unit.type &&
                index !== editingIndex &&
                isActiveDineInUnit(existingUnit)
        );
        if (isDuplicateType) return;

        if (editingIndex !== null) {
            const updatedUnits = units.map((existingUnit, index) =>
                index === editingIndex ? mergeEditedUnit(existingUnit, unit) : existingUnit
            );
            onUnitsChange(updatedUnits);
        } else {
            const deletedUnitIndex = units.findIndex(
                (existingUnit) => existingUnit.type === unit.type && existingUnit.quantity === 0
            );

            if (deletedUnitIndex !== -1) {
                const updatedUnits = units.map((existingUnit, index) =>
                    index === deletedUnitIndex
                        ? mergeEditedUnit(existingUnit, createNewDineInUnit(unit))
                        : existingUnit
                );
                onUnitsChange(updatedUnits);
            } else {
                onUnitsChange([...units, createNewDineInUnit(unit)]);
            }
        }
    };

    const handleOpenDeleteDialog = (index: number) => {
        setDeletingIndex(index);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDeletingIndex(null);
    };

    const handleConfirmDelete = () => {
        if (deletingIndex === null) return;

        const updatedUnits = units.map((unit, index) =>
            index === deletingIndex ? { ...unit, quantity: 0 } : unit
        );
        onUnitsChange(updatedUnits);
        handleCloseDeleteDialog();
    };

    return (
        <div className={styles.container}>
            <div className={styles.sectionHeader}>
                <div className={styles.sectionIntro}>
                    <p className={styles.label}>{t('My_Business.My_Services.Dine_In.Section_Title')}</p>
                    <p className={styles.description}>{t('My_Business.My_Services.Dine_In.Description')}</p>
                </div>
                <Button
                    title={t('My_Business.My_Services.Dine_In.Add_Unit')}
                    buttonType={ButtonTypes.Primery}
                    onClick={handleOpenAddDialog}
                    buttonStyle={styles.addButton}
                    leftIcon={PlusIcon}
                    disable={allUnitTypesUsed}
                />
            </div>

            {activeUnits.length > 0 && (
                <div className={styles.summaryBar}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{activeUnits.length}</span>
                        <span className={styles.summaryLabel}>{t('My_Business.My_Services.Dine_In.Unit_Types_Count')}</span>
                    </div>
                    <div className={styles.summaryDivider} />
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{totalCapacity}</span>
                        <span className={styles.summaryLabel}>{t('My_Business.My_Services.Dine_In.Total_Capacity')}</span>
                    </div>
                    <div className={styles.summaryDivider} />
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{generatedUnitsCount}</span>
                        <span className={styles.summaryLabel}>{t('My_Business.My_Services.Dine_In.QR_Ready_Count')}</span>
                    </div>
                </div>
            )}

            {activeUnits.length === 0 ? (
                <div className={styles.noDataWrapper}>
                    <NoData
                        message={t('My_Business.My_Services.Dine_In.No_Data')}
                        withButton={!allUnitTypesUsed}
                        buttonTitle={t('My_Business.My_Services.Dine_In.Add_Unit')}
                        onPressButton={handleOpenAddDialog}
                        containerStyle={styles.noDataContainer}
                    />
                </div>
            ) : (
                <div className={styles.unitsList}>
                    {units.map((unit, unitIndex) => {
                        if (!isActiveDineInUnit(unit)) {
                            return null;
                        }

                        const isGenerated = isUnitQrGenerated(unit);
                        const typeLabel = t(`My_Business.My_Services.Dine_In.Unit_Types.${unit.type}`);

                        return (
                            <div key={`${unit.type}-${unitIndex}`} className={styles.unitCard}>
                                <div className={styles.cardHeader}>
                                    <span className={styles.typeBadge}>{typeLabel}</span>
                                    <div className={styles.cardActions}>
                                        <button
                                            onClick={() => handleOpenEditDialog(unitIndex)}
                                            className={styles.iconButton}
                                            title={t('Buttons.Edit')}
                                            aria-label={t('Buttons.Edit')}
                                        >
                                            <PencilIcon width={18} height={18} color="var(--primary-button)" />
                                        </button>
                                        <button
                                            onClick={() => handleOpenDeleteDialog(unitIndex)}
                                            className={`${styles.iconButton} ${styles.deleteButton}`}
                                            title={t('Buttons.Delete')}
                                            aria-label={t('Buttons.Delete')}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <UnitQrStatusBadge unit={unit} />

                                <div className={styles.metaGrid}>
                                    <div className={styles.metaItem}>
                                        <span className={styles.metaLabel}>{t('My_Business.My_Services.Dine_In.Quantity')}</span>
                                        <span className={styles.metaValue}>{unit.quantity}</span>
                                    </div>
                                    <div className={styles.metaItem}>
                                        <span className={styles.metaLabel}>{t('My_Business.My_Services.Dine_In.Additional_Fee')}</span>
                                        <span className={styles.metaValue}>
                                            {unit.extra_cost != null && unit.extra_cost > 0
                                                ? `+${unit.extra_cost} ${t('Currency.AMD', '֏')}`
                                                : t('My_Business.My_Services.Dine_In.No_Fee')}
                                        </span>
                                    </div>
                                </div>

                                {isGenerated && unit.qr_data && (
                                    <div className={styles.qrPreview}>
                                        <p className={styles.qrPreviewTitle}>
                                            {t('My_Business.My_Services.Dine_In.QR_Codes_Ready', { count: unit.qr_data.length })}
                                        </p>
                                        <div className={styles.qrNumbers}>
                                            {unit.qr_data.map((qrItem) => (
                                                <span key={qrItem.number} className={styles.qrPill}>
                                                    #{qrItem.number}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {activeUnits.length > 0 && (
                <p className={styles.footerHint}>{t('My_Business.My_Services.Dine_In.Footer_Hint')}</p>
            )}

            <AddUnitDialog
                open={addDialogOpen}
                onClose={handleCloseAddDialog}
                onSave={handleSaveUnit}
                editingUnit={editingUnit}
                existingUnits={units}
                editingIndex={editingIndex}
            />

            <CustomDialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                {deletingUnit && (
                    <DeleteUnitDialog
                        unitTitle={getUnitDisplayTitle(deletingUnit, t)}
                        onDelete={handleConfirmDelete}
                        onClose={handleCloseDeleteDialog}
                    />
                )}
            </CustomDialog>
        </div>
    );
};

export default DineInUnits;
